import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Allergies.css';
import TitleIcon from '@/assets/icons/Icon-allergies.png';
import PeanutsIcon from '@/assets/icons/Peanuts.png';
import PenicillinIcon from '@/assets/icons/Penicillin.png';
import AnimalIcon from '@/assets/icons/Animal.png';
import DustIcon from '@/assets/icons/Dust.png';
import FishIcon from '@/assets/icons/Fish.png';

const iconMap = {
  Peanuts: PeanutsIcon,
  Dust: DustIcon,
  Penicillin: PenicillinIcon,
  Fish: FishIcon,
  'Animal dander': AnimalIcon,
};

const toneFromSeverity = (s) => {
  const val = String(s || '').toLowerCase();
  if (val === 'normal') return 'success';
  if (val === 'mild' || val === 'mild reaction') return 'warning';
  if (val === 'severe') return 'danger';
  return 'muted';
};

let uidSeq = 0;
const nextUid = () => `new-${Date.now().toString(36)}-${(uidSeq += 1)}`;

/* `uid` = clé React stable (une ligne garde la même identité pendant qu'on
   tape dedans). `id` = id backend, à conserver absolument : sans lui la
   sauvegarde ne peut pas distinguer une allergie existante (PUT) d'une
   nouvelle (POST) et finit par tout supprimer/recréer. */
const normalizeAllergies = (arr) =>
  (Array.isArray(arr) ? arr : []).map((x, idx) => {
    if (typeof x === 'string') {
      return { uid: `a-${idx}`, id: null, label: x, severity: 'Normal' };
    }
    const id = x?.id ?? x?.backendId ?? null;
    return {
      uid: x?.uid || (id != null ? `a-${id}` : `a-${idx}`),
      id,
      backendId: x?.backendId ?? id,
      version: x?.version,
      label: x?.label ?? x?.allergen ?? '',
      severity: x?.severity || x?.reaction || 'Normal',
    };
  });

// ✅ editable=true par défaut → mode édition affiché dès l'ouverture
const Allergies = ({ editable = true, onAllergiesChange, initialAllergies }) => {
  const { t } = useTranslation();

  // ⚠️ jamais de données de démo : ce sont des données médicales, une liste
  // vide doit rester vide (sinon on risque d'enregistrer de fausses allergies).
  const [allergies, setAllergies] = useState([]);

  // Hydrate depuis l'API quand initialAllergies change
  useEffect(() => {
    if (!initialAllergies) return;
    setAllergies(normalizeAllergies(initialAllergies));
  }, [initialAllergies]);

  const [newAllergy, setNewAllergy] = useState('');

  const pushChange = (updated) => {
    setAllergies(updated);
    onAllergiesChange?.(updated);
  };

  const handleDelete = (uid) => {
    pushChange(allergies.filter((a) => a.uid !== uid));
  };

  const handleFieldChange = (uid, field, value) => {
    pushChange(
      allergies.map((x) => (x.uid === uid ? { ...x, [field]: value } : x))
    );
  };

  const handleAdd = () => {
    const label = newAllergy.trim();
    if (!label) return;
    // pas d'`id` → la sauvegarde la créera (POST)
    pushChange([...allergies, { uid: nextUid(), id: null, label, severity: 'Normal' }]);
    setNewAllergy('');
  };

  /* Le brouillon de la ligne "ajouter" n'existait que dans cet état local :
     taper un nom puis cliquer directement sur "Save changes" le perdait
     silencieusement (rien n'était enregistré, aucune erreur). On le valide
     dès que le focus quitte le bloc d'ajout — sauf s'il reste à l'intérieur
     (tabulation vers l'année, clic sur "+"). */
  const commitDraftOnFocusOut = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    handleAdd();
  };

  return (
    <section className="alg-card">
      {/* Header */}
      <div className="alg-head">
        <div className="alg-title-icon">
          <img src={TitleIcon} alt="" width={24} height={24} />
        </div>
        <h4 className="alg-title">{t('allergies.title')}</h4>
      </div>

      <div className="alg-sep" />

      {/* ── VIEW mode ── */}
      {!editable ? (
        <div className="alg-chips">
          {allergies.map((c) => (
            <div
              key={c.uid}
              className={`alg-chip${c.label === 'Animal dander' ? ' wide' : ''}`}
            >
              <span className="chip-ibox">
                <img src={iconMap[c.label] || PeanutsIcon} alt="" width={12} height={12} />
              </span>
              <span className="chip-txt">{c.label}</span>
              {c.severity ? (
                <span className={`alg-badge alg-badge--${toneFromSeverity(c.severity)}`}>
                  {c.severity}
                </span>
              ) : null}
            </div>
          ))}
        </div>

      ) : (
      /* ── EDIT mode (default) ── */
        <div className="alg-edit">

          {/* Liste éditable */}
          <div className="alg-edit-list">
            {allergies.map((a) => (
              <div key={a.uid} className="alg-edit-item">
                {/* Nom */}
                <input
                  type="text"
                  value={a.label}
                  onChange={(e) => handleFieldChange(a.uid, 'label', e.target.value)}
                  placeholder={t('allergies.allergyName')}
                />
                {/* Sévérité */}
                <input
                  type="text"
                  value={a.severity || ''}
                  onChange={(e) => handleFieldChange(a.uid, 'severity', e.target.value)}
                  placeholder={t('allergies.severity')}
                />
                {/* Bouton X — toujours visible */}
                <button
                  className="alg-del-btn"
                  onClick={() => handleDelete(a.uid)}
                  aria-label={t('allergies.deleteButton')}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          {/* Ajout */}
          <div className="alg-add" onBlur={commitDraftOnFocusOut}>
            <input
              type="text"
              placeholder={t('allergies.addAllergyPlaceholder')}
              value={newAllergy}
              onChange={(e) => setNewAllergy(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
            <button onClick={handleAdd} aria-label={t('allergies.addButton')}>
              +
            </button>
          </div>

        </div>
      )}
    </section>
  );
};

export default Allergies;
